import {
  Component,
  Element,
  Event,
  EventEmitter,
  h,
  Method,
  Prop,
  State
} from '@stencil/core';
import { Host, HTMLStencilElement } from '@stencil/core/internal';

import { getInputVariantClass, InputVariant } from '../uui-input-utils';

// Have to copy this here due to storybook build issues.
function genID() {
  const prefix = 'uui';
  const randomNum = Math.random().toString(16).slice(2);
  return `${prefix}-${randomNum}`;
}

export type InputType =
  | 'date'
  | 'datetime-local'
  | 'text'
  | 'password'
  | 'range'
  | 'number'
  | 'email'
  | 'search';

  export type DateStringType = `${string}-${string}-${string}` | `${string}-${string}-${string} ${number}:${number}:${number}`
@Component({
  tag: 'uui-input',
  styleUrl: './uui-input.css',
  shadow: false,
  scoped: true
})
export class UUIInput {
  @Prop() inputID: string;
  @Prop() width: string = '100%';
  @Prop({ mutable: false, reflect: true }) autofocus: boolean = false;
  @Prop({ mutable: true, reflect: true }) value = '';
  @Prop({ attribute: 'proposedvalue', mutable: true, reflect: true })
  proposedValue = '';
  @Prop() error: boolean = false;
  @Prop() warning: boolean = false;
  @Prop() disabled: boolean = false;
  @Prop() name: string = '';
  @Prop() type: InputType = 'text';
  @Prop({ reflect: true }) placeholder: string = '';
  @Prop() min: number | DateStringType = 0;
  @Prop() max: number| DateStringType = 99999999;
  @Prop() maxlength: number = 524288;
  @Event({ eventName: 'acceptInput', bubbles: true, composed: true })
  acceptInput: EventEmitter;
  @Event({ eventName: 'textChange', bubbles: true, composed: true })
  textChange: EventEmitter;
  @Event({ eventName: 'inputBlur', bubbles: true, composed: true })
  inputBlur: EventEmitter;
  @Event({ eventName: 'inputFocus', bubbles: true, composed: true })
  inputFocus: EventEmitter;
  @Element() hostElement: HTMLStencilElement;
  @Prop() valid = true;
  @Prop() invalid = false;
  @Prop() list: string;
  @Prop() pattern: string;
  @Prop() autocomplete?: string;
  @Prop() step: number = 1;
  @State() hasFocus: boolean = false;

  constructor() {
    if (!this.inputID) {
      this.inputID = `${genID()}_${this.name}`;
    }
  }

  @Method()
  async clear() {
    this.value = '';
    this.proposedValue = '';
    const els = this.hostElement?.getElementsByTagName('input');
    for (const input of els) {
      (input as HTMLInputElement).value = '';
    }
  }

  componentDidRender() {
    // trigger on first render to position icons
    this.onStateChange();
  }

  onTextChange(event) {
    this.value = event?.target?.value ?? '';
    if (!this.value) {
      this.proposedValue = '';
    }
    this.valid = event?.target?.validity?.valid ?? false;
    this.invalid = !this.valid;
    this.textChange.emit(event?.target?.value);
  }

  forceFocus() {
    const els = this.hostElement?.getElementsByTagName('input');
    if (els?.length) {
      const input = els[0];
      input.focus();
    }
  }

  onKeyDown(e) {
    const ACCEPT_PROPOSED_KEYS = ['ArrowRight', 'End'];
    const ACCEPT_KEYS = ['Enter'];
    if (this.proposedValue && ACCEPT_PROPOSED_KEYS.includes(e.key)) {
      this.value = this.proposedValue;
    } else if (ACCEPT_KEYS.includes(e.key)) {
      e.preventDefault();
      // Since events are handled synchronously, get out of the
      // keydown handler
      setTimeout(() => {
        this.acceptInput.emit(this.value);
      }, 0);
    }
  }

  // it's likely icons will be added / removed between state changes
  onStateChange() {
    let hasStartIconSlot = !!this.hostElement.querySelector(
      '[slot="input-icon-start"]'
    );
    let hasEndIconSlot = !!this.hostElement.querySelector(
      '[slot="input-icon-end"]'
    );
    let input = this.hostElement.querySelector(`#${this.inputID}`);

    if (input) {
      if (hasEndIconSlot) {
        input.classList.add('uui-textbox-has-end-icon');
      } else {
        input.classList.remove('uui-textbox-has-end-icon');
      }
      if (hasStartIconSlot) {
        input.classList.add('uui-textbox-has-start-icon');
      } else {
        input.classList.remove('uui-textbox-has-start-icon');
      }
    }
  }

  get variant(): InputVariant {
    return getInputVariantClass({
      isError: this.error,
      isWarning: this.warning
    });
  }

  render() {
    const style = {
      width: this.width
    };

    const classList = `${this.variant}`;

    const {
      autocomplete,
      autofocus,
      inputID,
      type,
      list,
      name,
      disabled,
      invalid,
      pattern,
      placeholder,
      proposedValue,
      valid,
      value,
      maxlength,
      step
    } = this;
    let { min, max } = this;
    const numericTypes = ['number', 'date', 'range', 'datetime-local'];
    if (!numericTypes.includes(type)) {
      min = undefined;
      max = undefined;
    }
    const input = (
      <input
        id={inputID}
        autocomplete={autocomplete}
        type={type}
        class={classList}
        name={name}
        style={style}
        autofocus={autofocus}
        value={value}
        list={list}
        min={min}
        max={max}
        maxlength={maxlength}
        placeholder={placeholder}
        disabled={disabled}
        pattern={pattern}
        step={step}
        onInput={(event: UIEvent) => this.onTextChange(event)}
        onKeyDown={(event: UIEvent) => this.onKeyDown(event)}
        onChange={(event: UIEvent) => this.onTextChange(event)}
        onBlur={() => {
          this.inputBlur.emit();
          this.hasFocus = false;
        }}
        onFocus={() => {
          this.inputFocus.emit();
          this.hasFocus = true;
        }}
      >
        <slot />
      </input>
    );

    const hasProposed = type == 'text';

    const proposedStyle = hasProposed
      ? {
          ...style,
          backgroundColor: 'transparent',
          borderColor: 'transparent',
          boxShadow: 'none',
          opacity: 'var(--uui-disabled-opacity)',
          left: '0',
          pointerEvents: 'none',
          position: 'absolute',
          top: '0'
        }
      : undefined;

    const proposedInput =
      hasProposed && value ? (
        <input
          aria-hidden="true"
          value={proposedValue}
          type={type}
          class={classList}
          readonly
          tabindex="-1"
          style={proposedStyle}
        ></input>
      ) : null;
    return (
      <Host valid={'' + valid} invalid={'' + invalid}>
        <div
          style={{
            position: 'relative'
          }}
        >
          <div class="uui-slotted-icon-container uui-slotted-icon-container-start">
            <slot name="input-icon-start" />
          </div>
          {input}
          {proposedInput}
          <div class="uui-slotted-icon-container uui-slotted-icon-container-end">
            <slot name="input-icon-end" />
          </div>
        </div>
      </Host>
    );
  }
}
